"use client";

import { useCallback } from "react";
import type { MutableRefObject } from "react";
import {
  LogLevel,
  MobileModalDebugEvent,
  type PersistentDebugLogger,
  captureMobileModalEnvironment,
} from "@/hooks/telemetry";
import {
  TEAM_MODAL_CENTER_ORIGIN,
  TeamModalMode,
  useTeamModal,
} from "@/contexts/team-modal";
import type {
  HomePageInteractionState,
  OpenTeamDetailsInput,
  OpenTeamEditorInput,
} from "./domain/home-page/types";

type ClickInvestigationLogLocal = (
  message: string,
  payload: Record<string, unknown>,
  level: LogLevel,
) => void;

interface HomePageTeamDetailsModalHandlersLocal {
  openTeamDetails: (input: OpenTeamDetailsInput) => void;
  openTeamEditor: (input: OpenTeamEditorInput) => void;
}

interface UseHomePageTeamDetailsModalHandlersInput {
  clickInvestigationLog: ClickInvestigationLogLocal;
  latestInteractionState: MutableRefObject<HomePageInteractionState>;
  modalDebugLogger: PersistentDebugLogger;
}

export function useHomePageTeamDetailsModalHandlers({
  clickInvestigationLog,
  latestInteractionState,
  modalDebugLogger,
}: UseHomePageTeamDetailsModalHandlersInput): HomePageTeamDetailsModalHandlersLocal {
  const { openExisting } = useTeamModal();

  const openTeamDetails = useCallback(
    ({ teamId, morphSource }: OpenTeamDetailsInput) => {
      const { showTeamsModal, showTeamDetailsModal, teamDetailsTeamId } =
        latestInteractionState.current;
      modalDebugLogger.logEventFlow({
        name: MobileModalDebugEvent.OpenTeamDetailsRequested,
        payload: {
          teamId,
          previousTeamDetailsTeamId: teamDetailsTeamId,
          previousShowTeamsModal: showTeamsModal,
          previousShowTeamDetailsModal: showTeamDetailsModal,
          environment: captureMobileModalEnvironment(),
        },
      });
      clickInvestigationLog(
        "click-inv:openTeamDetails-entered",
        { teamId, teamIdType: typeof teamId },
        LogLevel.Info,
      );
      openExisting({
        teamId,
        mode: TeamModalMode.Present,
        origin: { morphSource: morphSource ?? null, aboveModals: false },
      });
      clickInvestigationLog(
        "click-inv:openTeamDetails-state-setters-called",
        { teamId },
        LogLevel.Info,
      );
      modalDebugLogger.logMarker({
        name: MobileModalDebugEvent.OpenTeamDetailsStateSettersCalled,
        payload: {
          teamId,
          environment: captureMobileModalEnvironment(),
        },
      });
    },
    [
      clickInvestigationLog,
      latestInteractionState,
      modalDebugLogger,
      openExisting,
    ],
  );

  // URL-restore only for now (`?viewTeam&mode=edit|copy`) — no UI writes
  // these modes yet. The surface itself resolves permission: edit falls back
  // to the regular state when the viewer lacks the Edit action.
  const openTeamEditor = useCallback(
    ({ teamId, editIntent }: OpenTeamEditorInput) => {
      openExisting({
        teamId,
        mode: TeamModalMode.Edit,
        editIntent,
        origin: TEAM_MODAL_CENTER_ORIGIN,
      });
    },
    [openExisting],
  );

  return {
    openTeamDetails,
    openTeamEditor,
  };
}
